import type { AddCommentType } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import type { CommentsDataState } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	ReplyData,
	CommentsDataMap,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { CommentActionType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { CommentType } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { expVal } from '@confluence/feature-experiments';
import { type SubscriptionsDispatchType } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import type { CommentInlineResolveProperties } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import type { UnreadCommentIDSetOperation } from '@confluence/unread-comments/entry-points/unreadCommentsTypes.ts';
import {
	isValidUnreadComment,
	buildValidationObjectFromProps,
} from '@confluence/unread-comments/entry-points/unreadCommentsUtils.ts';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { shouldSkipOwnPubSubEvent } from '@confluence/comments-util/entry-points/pubsubEventGuards.ts';

import {
	createCommentsPubSubExperienceTracker,
	CommentsPubSubSource,
	CommentsPubsubTrackedEventType,
	getCachedCommentVersionNumber,
} from '../utils/commentsPubSubExperienceTracking.ts';

type CombinedResolvedProperties = {
	inlineResolveProperties?: CommentInlineResolveProperties; // inline
	inlineText?: string; // inline
	resolved?: boolean; // footer
	resolvedFriendlyDate: string | null | undefined; // footer
	resolvedTime?: number | null; // footer
	resolvedUser?: string | null; // footer
};

// Shared callback type definitions
interface CommentCallbacks {
	addNewComment?: ({ currentUserMadeComment, newCommentId }: AddCommentType) => void;
	removeComment?: (commentId?: string) => void;
	addNewCommentThreads?: (newCommentThreads: CommentsDataMap) => void;
	addReplyToCommentThread?: (threadKey: string, reply: ReplyData, commentType: CommentType) => void;
	updateCommentBody?: (
		commentId: string,
		threadKey: string,
		newBodyValue: string,
		commentType: CommentType,
		displayName?: string | null,
	) => void;
	updateRemovedCommentIdsMap?: (props: {
		threadKey: string;
		commentId: string;
		commentActionType: CommentActionType.DELETE_COMMENT | CommentActionType.RESOLVE_COMMENT_THREAD;
		isReply: boolean;
		commentType: CommentType;
		resolvedProperties?: CombinedResolvedProperties;
	}) => void;
	reopenParentComment?: (props: {
		threadKey: string;
		commentType: CommentType;
		parentCommentId?: string;
	}) => void;
	getCurrentCommentsPanelState?: () => CommentsDataState;
	updateUnreadCommentIDSet?: (
		unreadCommentIDSet: Set<string>,
		operation?: UnreadCommentIDSetOperation,
	) => void;
	getViewedCommentsCacheOnly?: () => { success: boolean; data?: Set<string>; error?: Error };
	addCommentInCache?: ({
		newComment,
		onError,
	}: {
		newComment: any;
		onError?: (error: Error) => void;
	}) => void;
	deleteCommentInCache?: ({
		commentId,
		onError,
	}: {
		commentId: string;
		onError?: (error: Error) => void;
	}) => boolean;
	clearReplyIfRemoved?: (props: { commentId: string }) => void;
}

type OnPubSubEventFn = (
	input: {
		userId: string | null;
		contentId: string;
		updateQueryVariables?: {
			contentId: string;
			isReactionsEnabled: boolean;
		};
	} & CommentCallbacks,
) => (event: string, payload: any) => void;

type AddSubscriberPubsubParams = {
	addSubscriber?: SubscriptionsDispatchType['addSubscriber'];
	setContentType?: SubscriptionsDispatchType['setContentType'];
	setContentId?: SubscriptionsDispatchType['setContentId'];
} & PubSubParams;

type RemoveSubscriberPubsubParams = {
	removeSubscriber?: SubscriptionsDispatchType['removeSubscriber'];
	subscriptions: Array<{ subscriberId: string; delta: string }>;
};

type PubSubParams = {
	contentId: string;
	contentType?: string | null;
	userId: string | null;
	addNewComment: ({ currentUserMadeComment, newCommentId }: AddCommentType) => void;
	removeComment: (commentId?: string) => void;
	queryArgs: {
		isReactionsEnabled: boolean;
	};
} & CommentCallbacks;

const FETCH_COMMENT_RETRY_DELAYS_MS = [500, 1000, 2000];

/**
 * Fetches a comment by ID with automatic retry on null result.
 * The pubsub event can arrive before the server has committed the comment,
 * causing fetchCommentById to return null. We retry with backoff to handle this.
 */
const fetchCommentByIdWithRetry = async ({
	commentId,
	contentId,
}: {
	commentId: string;
	contentId: string;
}): ReturnType<typeof fetchCommentById> => {
	let result = await fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
	if (result !== null) {
		return result;
	}

	for (const delayMs of FETCH_COMMENT_RETRY_DELAYS_MS) {
		// eslint-disable-next-line no-await-in-loop
		await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
		// eslint-disable-next-line no-await-in-loop
		result = await fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
		if (result !== null) {
			return result;
		}
	}

	return result;
};

const handleUnreadCommentsForReopenedComment = ({
	comment,
	userId,
	viewedComments,
	updateUnreadCommentIDSet,
}: {
	comment: any;
	userId: string;
	viewedComments: Set<string>;
	updateUnreadCommentIDSet: (
		unreadCommentIDSet: Set<string>,
		operation?: UnreadCommentIDSetOperation,
	) => void;
}) => {
	const validUnreadCommentIds = new Set<string>();

	// Validate the main comment
	const commentForValidation = buildValidationObjectFromProps({
		commentId: comment.id,
		userId: comment.author?.__typename === 'KnownUser' ? comment.author.accountId : undefined,
		createdAt: comment.createdAt?.value || undefined,
		commentType: 'FOOTER', // Page comments are always FOOTER type
		resolveProperties: { resolved: false }, // reopened comment = resolved false
	});

	if (
		isValidUnreadComment({
			comment: commentForValidation,
			currentUserId: userId,
			viewedComments,
		})
	) {
		validUnreadCommentIds.add(comment.id);
	}

	// Validate replies if they exist
	if (comment.replies) {
		comment.replies.forEach((reply: any) => {
			const replyForValidation = buildValidationObjectFromProps({
				commentId: reply.id,
				userId: reply.author?.__typename === 'KnownUser' ? reply.author.accountId : undefined,
				createdAt: reply.createdAt?.value || undefined,
				commentType: 'FOOTER', // Page comments are always FOOTER type
				resolveProperties: { resolved: false }, // reopened comment = resolved false
			});

			if (
				isValidUnreadComment({
					comment: replyForValidation,
					currentUserId: userId,
					viewedComments,
				})
			) {
				validUnreadCommentIds.add(reply.id);
			}
		});
	}

	updateUnreadCommentIDSet(validUnreadCommentIds, 'add');
};

export const onEvent: OnPubSubEventFn =
	({
		userId,
		contentId,
		updateRemovedCommentIdsMap,
		reopenParentComment,
		getViewedCommentsCacheOnly,
		updateUnreadCommentIDSet,
		addCommentInCache,
		deleteCommentInCache,
		clearReplyIfRemoved,
	}) =>
	(event, payload) => {
		if (!event) {
			return;
		}
		const { accountId, originTabId } = payload;
		const commentRequestBody = payload?.[event];
		const { commentId } = commentRequestBody;

		if (
			shouldSkipOwnPubSubEvent({
				accountId,
				currentUserId: userId,
				originTabId,
			})
		) {
			return;
		}

		const {
			start: startCommentsPubSubExperience,
			fail: failCommentsPubSubExperience,
			handleCompletePubSubExperiences,
		} = createCommentsPubSubExperienceTracker(CommentsPubSubSource.PageCommentsPubSub, commentId);

		startCommentsPubSubExperience(event);

		try {
			switch (event) {
				case 'commentCreated':
					if (addCommentInCache && commentId) {
						const fetchFn = expVal('cc_comments_page_comment_retry', 'isEnabled', false)
							? fetchCommentByIdWithRetry
							: ({ commentId: cId, contentId: cntId }: { commentId: string; contentId: string }) =>
									fetchCommentById({
										commentId: cId,
										contentId: cntId,
										fetchPolicy: 'network-only',
									});
						void fetchFn({ commentId, contentId })
							.then((comment) => {
								if (comment) {
									addCommentInCache({
										newComment: comment,
										onError: failCommentsPubSubExperience,
									});
									return handleCompletePubSubExperiences();
								} else {
									failCommentsPubSubExperience(
										new Error(`commentCreated: comment ${commentId} not found after retries`),
									);
								}
							})
							.catch((err: unknown) => {
								failCommentsPubSubExperience(err);
							});
					} else {
						failCommentsPubSubExperience(
							new Error('commentCreated: addCommentInCache or commentId is not defined'),
						);
					}

					if (updateUnreadCommentIDSet && accountId !== userId) {
						updateUnreadCommentIDSet(new Set([commentId]), 'add');
					}

					break;
				case 'commentResolved':
					if (commentId) {
						// fetchCommentById implicitly updates useAllCommentsDataQuery cache
						void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' })
							.then((comment) => {
								clearReplyIfRemoved?.({ commentId });
								// Handle unread comments after fetchCommentById completes
								const { replies = [] } = commentRequestBody;

								if (updateUnreadCommentIDSet) {
									updateUnreadCommentIDSet(new Set([commentId, ...replies]), 'remove');
								}

								return handleCompletePubSubExperiences([
									{ comment, type: CommentsPubsubTrackedEventType.Resolve },
								]);
							})
							.catch((err: unknown) => {
								failCommentsPubSubExperience(err);
							});
					} else {
						failCommentsPubSubExperience(new Error('commentResolved: commentId is not defined'));
					}
					// Only commentActionType and commentId are used; the rest of the variables here are defaults
					if (updateRemovedCommentIdsMap && commentId) {
						updateRemovedCommentIdsMap({
							threadKey: commentId,
							commentId,
							commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
							isReply: false,
							commentType: CommentType.GENERAL,
						});
					}

					break;
				case 'commentDeleted':
					if (deleteCommentInCache && commentId) {
						const deletionSuccessful = deleteCommentInCache({
							commentId,
							onError: failCommentsPubSubExperience,
						});

						if (deletionSuccessful) {
							clearReplyIfRemoved?.({ commentId });
						}
					}

					if (updateUnreadCommentIDSet) {
						updateUnreadCommentIDSet(new Set([commentId]), 'remove');
					}
					// Only commentActionType and commentId are used; the rest of the variables here are defaults
					if (updateRemovedCommentIdsMap && commentId) {
						updateRemovedCommentIdsMap({
							threadKey: commentId,
							commentId,
							commentActionType: CommentActionType.DELETE_COMMENT,
							isReply: false,
							commentType: CommentType.GENERAL,
						});
					}
					void handleCompletePubSubExperiences().catch((err: unknown) => {
						failCommentsPubSubExperience(err);
					});

					break;
				case 'commentReopened':
					if (commentId) {
						// fetchCommentById implicitly updates useAllCommentsDataQuery cache
						void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' })
							.then((commentData) => {
								// Handle unread comments with the fetched comment data
								if (
									updateUnreadCommentIDSet &&
									commentData &&
									userId &&
									getViewedCommentsCacheOnly
								) {
									const viewedCommentsResult = getViewedCommentsCacheOnly();
									if (viewedCommentsResult.success && viewedCommentsResult.data) {
										handleUnreadCommentsForReopenedComment({
											comment: commentData,
											userId,
											viewedComments: viewedCommentsResult.data,
											updateUnreadCommentIDSet,
										});
									}
								}

								return handleCompletePubSubExperiences([
									{ comment: commentData, type: CommentsPubsubTrackedEventType.Reopen },
								]);
							})
							.catch((err: unknown) => {
								failCommentsPubSubExperience(err);
							});
					} else {
						failCommentsPubSubExperience(new Error('commentReopened: commentId is not defined'));
					}
					// Remove from commentThreadsRemovedByOtherUsers when reopened
					if (commentId && reopenParentComment) {
						reopenParentComment({
							threadKey: commentId,
							commentType: CommentType.GENERAL,
							parentCommentId: commentId,
						});
					}
					break;
				case 'commentUpdated':
					if (commentId) {
						const previousVersionNumber = getCachedCommentVersionNumber(commentId);
						// fetchCommentById implicitly updates useAllCommentsDataQuery cache
						void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' })
							.then((comment) => {
								return handleCompletePubSubExperiences([
									{
										comment,
										type: CommentsPubsubTrackedEventType.Edit,
										previousVersionNumber,
									},
								]);
							})
							.catch((err: unknown) => {
								failCommentsPubSubExperience(err);
							});
					} else {
						failCommentsPubSubExperience(new Error('commentUpdated: commentId is not defined'));
					}
					break;
				default:
					failCommentsPubSubExperience(new Error(`Unhandled comments pubsub event: ${event}`));
					break;
			}
		} catch (error: unknown) {
			failCommentsPubSubExperience(error);
			throw error;
		}
	};

export const setUpPageCommentsPubSub = ({
	contentId,
	contentType,
	userId,
	addNewComment,
	removeComment,
	queryArgs: { isReactionsEnabled },
	addNewCommentThreads,
	addReplyToCommentThread,
	updateCommentBody,
	updateRemovedCommentIdsMap,
	reopenParentComment,
	getCurrentCommentsPanelState,
	addSubscriber,
	setContentType,
	setContentId,
	updateUnreadCommentIDSet,
	getViewedCommentsCacheOnly,
	addCommentInCache,
	deleteCommentInCache,
	clearReplyIfRemoved,
}: AddSubscriberPubsubParams):
	| {
			subscriberId: string;
			delta: string;
	  }[]
	| undefined => {
	const pubSubResourceType = contentType || 'page';
	const subscriberIds: { subscriberId: string; delta: string }[] = [];
	if (!addSubscriber || !setContentType || !setContentId) {
		return;
	}
	setContentType(pubSubResourceType);
	setContentId(contentId);
	const subscribers = [
		{
			subscriberId: 'pageCommentsPubSubCommentCreated',
			onSubscriptionData: onEvent({
				userId,
				contentId,
				addNewComment,
				addNewCommentThreads,
				addReplyToCommentThread,
				updateUnreadCommentIDSet,
				addCommentInCache,
			}),
			delta: 'commentCreated',
		},
		{
			subscriberId: 'pageCommentsPubSubCommentResolved',
			onSubscriptionData: onEvent({
				userId,
				contentId,
				addNewComment,
				updateRemovedCommentIdsMap,
				updateUnreadCommentIDSet,
				clearReplyIfRemoved,
			}),
			delta: 'commentResolved',
		},
		{
			subscriberId: 'pageCommentsPubSubCommentDeleted',
			onSubscriptionData: onEvent({
				userId,
				contentId,
				removeComment,
				updateRemovedCommentIdsMap,
				getCurrentCommentsPanelState,
				updateUnreadCommentIDSet,
				deleteCommentInCache,
				clearReplyIfRemoved,
			}),
			delta: 'commentDeleted',
		},
		{
			subscriberId: 'pageCommentsPubSubCommentReopened',
			onSubscriptionData: onEvent({
				userId,
				contentId,
				addNewComment,
				addNewCommentThreads,
				reopenParentComment,
				getCurrentCommentsPanelState,
				getViewedCommentsCacheOnly,
				updateUnreadCommentIDSet,
			}),
			delta: 'commentReopened',
		},
		{
			subscriberId: 'pageCommentsPubSubCommentUpdated',
			onSubscriptionData: onEvent({
				userId,
				contentId,
				updateQueryVariables: {
					contentId,
					isReactionsEnabled,
				},
				updateCommentBody,
			}),
			delta: 'commentUpdated',
		},
	];
	for (const subscriber of subscribers) {
		subscriberIds.push({ subscriberId: addSubscriber(subscriber), delta: subscriber.delta });
	}
	return subscriberIds;
};

export const removePageCommentsPubSub = ({
	removeSubscriber,
	subscriptions,
}: RemoveSubscriberPubsubParams): void => {
	const eventsToUnsubscribe = ['commentCreated', 'commentDeleted', 'commentUpdated'];

	if (!removeSubscriber || !subscriptions?.length) {
		return;
	}
	for (const subscriber of subscriptions) {
		if (eventsToUnsubscribe.includes(subscriber.delta)) {
			removeSubscriber(subscriber);
		}
	}
};
